import React, { useState, useEffect } from 'react';
import { getTechPageData } from '../services/techService';
import { getStrapiMedia, renderSafeText } from '../lib/strapi';
import { Cpu, Share2, ShieldCheck, Zap } from 'lucide-react';

const Technology = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getTechPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Technology...</p>
      </div>
    );
  }

  const heroImageUrl = getStrapiMedia(data.hero_Image);

  return (
    <div className="bg-white">
      {/* HERO SECTION */}
      <section className="relative overflow-hidden min-h-[60vh] lg:min-h-[85vh] flex items-center justify-center">
        {heroImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
            {/* Lighter overlay to let the image pop while keeping white text readable */}
            <div className="absolute inset-0 bg-black/40" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-50" />
        )}
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1200px]">
          <h1 className={`text-[46px] md:text-[64px] lg:text-[80px] font-bold tracking-tight leading-[1.1] max-w-5xl mx-auto font-heading ${heroImageUrl ? 'text-white' : 'text-widya-charcoal'}`}>
            {renderSafeText(data.hero_title)}
          </h1>
        </div>
      </section>

      {/* WIDYACONNECT SECTION */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto max-w-[1100px]">
          <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-50 flex flex-col lg:flex-row overflow-hidden">
            <div className="lg:w-[55%] p-12 lg:p-16 flex flex-col justify-center">
              <h2 className="text-[36px] md:text-[42px] font-black text-widya-charcoal mb-4 font-heading leading-tight tracking-[-1px]">
                {renderSafeText(data.widya_connect_title)}
              </h2>
              <div className="w-12 h-[3px] bg-widya-green mb-8" />
              <p className="text-[17px] text-[#555555] leading-[28px] mb-10">
                {renderSafeText(data.widya_connect_description)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 justify-items-start">
                {data.widya_connect_features?.map((item, i) => (
                  <div key={i} className="inline-flex items-center justify-center px-5 py-3 bg-[#67ba8e] text-white rounded-[8px] text-[16px] xl:text-[18px] font-bold tracking-[-1px] shadow-sm">
                    {renderSafeText(item.feature_name)}
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-[45%] relative min-h-[350px] lg:min-h-full">
              <img src={getStrapiMedia(data.widya_connect_logo)} alt="WIDYAConnect" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* HARVESTFLOW SECTION */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto max-w-[1100px]">
          <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-50 flex flex-col lg:flex-row-reverse overflow-hidden">
            <div className="lg:w-[55%] p-12 lg:p-16 flex flex-col justify-center">
              <h2 className="text-[36px] md:text-[42px] font-black text-widya-charcoal mb-4 font-heading leading-tight tracking-[-1px]">
                {renderSafeText(data.harvest_flow_title)}
              </h2>
              <div className="w-12 h-[3px] bg-widya-green mb-8" />
              <p className="text-[17px] text-[#555555] leading-[28px] mb-10">
                {renderSafeText(data.harvest_flow_description)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 justify-items-start">
                {data.harvest_flow_features?.map((item, i) => (
                  <div key={i} className="inline-flex items-center justify-center px-5 py-3 bg-[#67ba8e] text-white rounded-[8px] text-[16px] xl:text-[18px] font-bold tracking-[-1px] shadow-sm">
                    {renderSafeText(item.feature_name)}
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-[45%] relative min-h-[350px] lg:min-h-full">
              <img src={getStrapiMedia(data.harvest_flow_logo)} alt="HarvestFlow" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PREDICTIVE ANALYTICS SECTION */}
      <section className="py-24 px-6 text-center bg-white">
        <div className="container mx-auto max-w-[1200px]">
          <h3 className="text-[36px] md:text-[46px] lg:text-[52px] font-black text-widya-charcoal mb-6 font-heading leading-[1.1] tracking-[-1px] max-w-4xl mx-auto">
            {renderSafeText(data.matching_engine_title)}
          </h3>
          <div className="w-16 h-[3px] bg-widya-green mx-auto mb-8" />
          <p className="text-[18px] text-[#555555] leading-[28px] max-w-3xl mx-auto mb-16">
            {renderSafeText(data.matching_engine_description)}
          </p>
          {data.matching_engine_image && (
            <img
              src={getStrapiMedia(data.matching_engine_image)}
              alt="Predictive Analytics"
              className="mx-auto w-full max-w-[1100px] h-auto object-contain"
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default Technology;